import type { ReportFilter } from '../models/filter.types'
import type { SectionBuild } from '../models/layoutConfig.types'

// One typed source for the targeting request shapes shared by the standalone Meta/TikTok
// reports and Demographic Analysis. Production v3 moved these panels under Demographic Analysis
// in c8244add; keeping their request builders here prevents the two route configs drifting again.

export const META_CHANNEL_FILTER: ReportFilter = {
  field: 'marketing_channel',
  match_mode: 'equals',
  value: 'meta_ads',
}

export const TIKTOK_CHANNEL_FILTER: ReportFilter = {
  field: 'marketing_channel',
  match_mode: 'equals',
  value: 'tiktok_ads',
}

const META_TARGETING_TAB_DATA: Record<
  string,
  { chartDimensions: string[]; filters?: ReportFilter[]; partitionOnly?: boolean }
> = {
  Objectives: { chartDimensions: ['objective'] },
  Placements: { chartDimensions: ['publisher_platform', 'platform_position'] },
  Device: { chartDimensions: ['impression_device'] },
  'Age & Gender': {
    chartDimensions: ['age', 'user_gender'],
    filters: [META_CHANNEL_FILTER],
    partitionOnly: true,
  },
  Location: {
    chartDimensions: ['country', 'city_name'],
    filters: [META_CHANNEL_FILTER],
    partitionOnly: true,
  },
}

const TIKTOK_TARGETING_TAB_DATA: Record<
  string,
  { chartDimensions: string[]; filters?: ReportFilter[]; partitionOnly?: boolean }
> = {
  Behavior: { chartDimensions: ['behavior_name'] },
  Interest: { chartDimensions: ['interest_category'] },
  'Age & Gender': {
    chartDimensions: ['age', 'user_gender'],
    filters: [TIKTOK_CHANNEL_FILTER],
    partitionOnly: true,
  },
  Location: {
    chartDimensions: ['city_name'],
    filters: [TIKTOK_CHANNEL_FILTER],
    partitionOnly: true,
  },
  Device: { chartDimensions: ['device_brand'] },
  Placement: { chartDimensions: ['placement_type'] },
  'Search Terms': { chartDimensions: ['search_term'] },
}

// v3 `getAudienceConfig`. The chart and table are simultaneous (`visibleType:'double'`).
export const metaAudienceBuild: SectionBuild = () => ({
  id: '19-audiences',
  reportId: 19,
  scheduleTabs: ['metaads_customaudiences'],
  view: 'table',
  composition: 'chart-table',
  chartDimensions: ['audience_name'],
  chartMetrics: ['audience_size_lower_bound', 'audience_size_upper_bound'],
  sortField: 'audience_size_lower_bound',
})

// v3 Meta `targetingPerformanceConfig`.
export const metaTargetingPerformanceBuild: SectionBuild = ({ tab }) => {
  const data = META_TARGETING_TAB_DATA[tab.name] ?? META_TARGETING_TAB_DATA['Objectives']!
  return {
    id: `19-targeting-performance-${tab.name}`,
    reportId: tab.reportId ?? 19,
    scheduleTabs: tab.tabs ?? [],
    view: 'table',
    chartMetrics: ['cost', 'reach'],
    chartDimensions: data.chartDimensions,
    sortField: 'cost',
    filters: data.filters ?? [],
    ...(data.partitionOnly ? { partitionOnly: true } : {}),
  }
}

// v3 TikTok `targetingPerformanceConfig` / Demographic `tiktokTargetingPerformanceConfig`.
export const tiktokTargetingPerformanceBuild: SectionBuild = ({ tab }) => {
  const scheduleKey = tab.tabs?.[0]
  const sourceName =
    scheduleKey === 'tiktokads_demographic_interest_category_data'
      ? 'Interest'
      : scheduleKey === 'tiktokads_demographic_search_terms_data'
        ? 'Search Terms'
        : scheduleKey === 'tiktokads_demographic_device_data'
          ? 'Device'
          : tab.name
  const data = TIKTOK_TARGETING_TAB_DATA[sourceName] ?? TIKTOK_TARGETING_TAB_DATA['Behavior']!
  return {
    // v3 id had no separator before the tab name — kept verbatim (persistence/cache key).
    id: `45-targeting-performance${tab.name}`,
    reportId: tab.reportId ?? 45,
    scheduleTabs: tab.tabs ?? [],
    view: 'table',
    composition: 'chart-table',
    chartMetrics: ['cost', 'clicks_destination', 'conversions', 'cost_per_conversion'],
    chartDimensions: data.chartDimensions,
    sortField: 'purchase_value',
    filters: data.filters ?? [],
    ...(data.partitionOnly ? { partitionOnly: true } : {}),
  }
}

// v3 Demographic `getDeviceConfig`. Its section deliberately has no reportId: the Meta and
// TikTok tabs must be gated independently by their own report 19/45 schedules.
export const demographicDeviceBuild: SectionBuild = ({ tab }) => {
  const isMeta = tab.reportId === 19
  const data = isMeta ? META_TARGETING_TAB_DATA['Device']! : TIKTOK_TARGETING_TAB_DATA['Device']!
  return {
    id: `${tab.reportId ?? 19}-device`,
    reportId: tab.reportId ?? 19,
    scheduleTabs: tab.tabs ?? [],
    view: 'table',
    composition: 'chart-table',
    chartDimensions: data.chartDimensions,
    chartMetrics: isMeta
      ? ['cost', 'reach']
      : ['cost', 'clicks_destination', 'conversions', 'cost_per_conversion'],
    sortField: 'cost',
    filters: [],
  }
}
